import { Component, OnInit, Input } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { MatDialog} from '@angular/material/dialog';
import { PageEvent} from '@angular/material/paginator';
import { InventoryFormComponent } from '../inventory-form/inventory-form.component';
import { LoanerWheelInventoryState } from '../../../store/state/loaner-wheel-inventory.state';
import { Store, select } from '@ngrx/store';
import { GetProviderLocations, GetModelTypes, GetTireSizes } from '../../../store/actions/loaner-wheel-inventory.action';
import { SelectProviderLocations, SelectModelTypes, SelectTireSizes } from '../../../store/app.selector';
import { ProviderLocation, Inventory, ModelType, TireSize } from '../../../models/loaner-wheel-inventory';
import { LoanWheelInventoryService } from '../../../services/loaner-wheel-inventory.service';
import { InventoryHistoryComponent } from '../inventory-history/inventory-history.component';
import { TslDialog } from '@tesla/ng-tds';
import { I18nService } from '../../../../shared/services/i18n.service';

@Component({
  selector: 'app-inventory-grid',
  templateUrl: './inventory-grid.component.html',
  styleUrls: ['./inventory-grid.component.scss']
})
export class InventoryGridComponent implements OnInit {
  @Input() supplierCode: string;
  datasource: BehaviorSubject<any> = new BehaviorSubject([]);

  displayedColumns = ['location', 'model', 'tiresize', 'quantity', 'actions'];
  pageSizeOptions: number[] = [10, 25, 50, 100, 250];
  pageIndex = 0;
  pageSize = 10;
  total = 0;

  inventoryList: any[] = [];
  locationAddress: any[] = [];
  modelTypes: ModelType[] = [];
  tireSizes: TireSize[] = [];
  constructor(private matDialog: MatDialog,
    private store: Store<LoanerWheelInventoryState>,
    private inventoryService: LoanWheelInventoryService,
    public translateService: I18nService,
    private dialog: TslDialog) {
  }

  ngOnInit() {
    this.loadResources();
  }

  loadResources(): void {
    this.store.dispatch(new GetModelTypes());
    this.store.dispatch(new GetTireSizes());
    this.store.dispatch(new GetProviderLocations(this.supplierCode));
    combineLatest(
      [this.store.pipe(select(SelectModelTypes)),
      this.store.pipe(select(SelectTireSizes))]
    ).subscribe(([types, sizes]) => {
      this.modelTypes = types;
      this.tireSizes = sizes;
    });

    this.store.pipe(select(SelectProviderLocations)).subscribe(loc => {
      if (loc && loc.length > 0) {
        this.formatAddress(loc);
        this.loadTabledata();
      }
    });
  }

  loadTabledata() {
    var inventory: Inventory[] = [];
    this.inventoryList = [];
    this.inventoryService.getInventoryBySupplier(this.supplierCode).subscribe(i => {
      inventory.unshift(...i);
      if (inventory && inventory.length > 0 &&
        this.locationAddress && this.locationAddress.length > 0) {
        this.locationAddress.forEach(x => {
          if (inventory.filter(d => d.locationId == x.locationId).length > 0) {
            this.inventoryList.push({ isGroup: true, Id: x.locationId, name: x.address });
            this.inventoryList = this.inventoryList.concat(inventory.filter(d => d.locationId == x.locationId));
          }
        });
      }
      this.loadGrid();
    });
  }

  formatAddress(locations: ProviderLocation[]) {
    this.locationAddress = [];
    this.locationAddress = this.locationAddress.concat(locations.map(x => {
      return {
        locationId: x.locationId,
        address: this.GetFullAddress(x.address)
      }
    }));
  }

  GetFullAddress(address: any): string {
    if (address) {
      return [this.deepfind(address.addressLine1),
      this.deepfind(address.addressLine2),
      this.deepfind(address.street3), this.deepfind(address.city), this.deepfind(address.province),
      this.deepfind(address.postalCode),
      this.deepfind(address.countryCode)].filter(x => x).join(', ');
    }
  }

  deepfind(obj) {
    if (typeof obj !== 'undefined' || obj !== null) {
      return obj;
    }
  }

  loadGrid() {
    this.total = this.inventoryList.length;
    const skip = this.pageIndex * this.pageSize;
    const take = (this.pageIndex + 1) * this.pageSize;
    this.datasource.next(this.inventoryList.slice(skip, take));
  }

  onPageChange(pageChange: PageEvent): void {
    const { pageIndex, pageSize } = pageChange;
    this.pageIndex = pageIndex;
    this.pageSize = pageSize;
    this.loadGrid();
  }

  openForm(item: Inventory): void {
    const dialogRef = this.matDialog.open(InventoryFormComponent, {
      width: '58%',
      minWidth: '600px',
      data: { supplierCode: this.supplierCode, locations: this.locationAddress, item: item }
    });

    dialogRef.afterClosed().subscribe((dataItem: Inventory) => {
      if (dataItem) {
        dataItem.supplierCode = this.supplierCode;
        if (item == null && this.inventoryList && this.inventoryList.filter(x => x.supplierCode === dataItem.supplierCode &&
          x.locationId === dataItem.locationId && x.modelTypeId === dataItem.modelTypeId && x.tireSizeId === dataItem.tireSizeId).length > 0) {
          this.dialog.alert(this.translateService.get('Alert'), this.translateService.get('There is already an inventory entry for this tire size and model type. Please edit the quantity.'));
        }
        else {
          this.inventoryService.setInventory(dataItem).subscribe(x => {
            if (x) {
              this.loadTabledata();
            }
          });
        }
      }
    });
  }

  getFullLocation(id: string): string {
    let loc = null;
    if (this.locationAddress && this.locationAddress.length > 0) {
      loc = this.locationAddress.find(x => x.locationId == id).address;
    }
    return loc;
  }

  getModelName(id: number): string {
    let model = null;
    if (this.modelTypes && this.modelTypes.length > 0) {
      let modelInfo = this.modelTypes.find(t => t.id == id);
      model = modelInfo.variantProviderText ? modelInfo.name +" - "+ modelInfo.variantProviderText : modelInfo.name;
    }
    return this.translateService.get(model);
  }

  getTireSize(id: number): string {
    let tire = null;
    if (this.tireSizes && this.tireSizes.length > 0) {
      tire = this.tireSizes.find(t => t.id == id).name;
    }
    return this.translateService.get(tire);
  }

  isHeader(index, item): boolean {
    // returns if row item is a header
    return item.isGroup;
  }


  showHistory() {
    const dialogRef = this.matDialog.open(InventoryHistoryComponent, {
      width: '90%',
      minWidth: '630px',
      maxHeight: '800px',
      data: { supplierCode: this.supplierCode, locations: this.locationAddress }
    });
  }
}
